import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { selectedAccStateSelector } from '@core/store/library-stores';
import { useAppSelector } from '@core/store/store';
import { Button, ConfirmButton, CustomSelect, CustomSelectItem, LoadingData, StyledSlider } from '@hwm/ui-lib';
import { SiderKeys, useSiderAnimation, useSiderContent } from './Sidebar.utils';
import { setSiteIdURL } from '@shared/hooks/useSiteIdStateQuery';
import { useHistory } from 'react-router-dom';
import { AppPaths } from '@core/routes/routesPaths';
import { t, Trans } from '@lingui/macro';
import { usePmZoneSiteListDataSource, useReportsDataSource } from '@features/dataSource';
import { Dialog, makeStyles, Theme, Typography } from '@material-ui/core';
import { mapRangeTypeToTimePeriod, RangeType, TimePeriodOptionsFn } from '@shared/models';
import { CustomHeader } from '@shared/components';
import { useSidebar } from '@shared/hooks/useSider';
import { isEmpty, isNil } from 'lodash';

const useStyles = makeStyles((theme: Theme) => ({
  select: {
    height: 40,
    maxHeight: 40,
    borderWidth: 2,
    zIndex: 200,
    margin: theme.spacing(0, 0, 1),
    width: 200,
    '& .select__control': {
      minWidth: 116,
    },
  },
  dialog: {
    width: 300,
    maxWidth: 300,
    padding: theme.spacing(1, 3, 4),
  },
  buttonsWrapper: {
    height: 36,
    minHeight: 36,
    '& button': {
      margin: theme.spacing(0, 3, 0, 0),
      height: 32,
      width: 80,
      minWidth: 80,
    },
    [theme.breakpoints.down('xs')]: {
      height: 32,
      '& button': {
        height: 28,
      },
    },
  },
}));

export const Sidebar: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();

  const TimePeriodOptions = useMemo(() => TimePeriodOptionsFn(), []);

  const { sidebarState, sidebarSection, handleSidebarSection } = useSidebar();
  const { showData } = useSiderAnimation(false);

  const { selectedAccount } = useAppSelector(selectedAccStateSelector);
  const { id: accountId, name, isPmZone } = selectedAccount ?? {};
  const { pmZoneSites } = usePmZoneSiteListDataSource();

  const { siderContent, loadingData } = useSiderContent(
    selectedAccount,
    selectedAccount?.accountParentId ?? selectedAccount?.parentId ?? selectedAccount?.id
  );

  useEffect(() => {
    if (Boolean(sidebarSection === SiderKeys.accHierarchy && isPmZone && accountId)) {
      handleSidebarSection(SiderKeys.pmzDetails);
      history.push(AppPaths.graphsView.path.base);
    }
    setSiteIdURL('');
  }, [isPmZone, accountId, history, sidebarSection, handleSidebarSection]);

  const content = useMemo(() => {
    return sidebarSection ? siderContent[sidebarSection] : loadingData;
  }, [sidebarSection, siderContent, loadingData]);

  const [showModal, setShowModal] = useState(false);
  const { handleGetFleetReport, handleGetActuationsReport, handleGetMaintenanceReport } = useReportsDataSource();
  const [rangeState, setRangeState] = useState<RangeType>(RangeType.LastMonth);
  const [callInDays, setCallInDays] = useState(7);

  const noPrvToOpenControlSetting = useMemo(() => {
    return (
      !pmZoneSites ||
      isEmpty(pmZoneSites) ||
      !pmZoneSites?.find((s) => s.siteType === 'PRV' && (s.isAssignedToPmZone || !s.isAssignedToOtherPmZone))
    );
  }, [pmZoneSites]);

  useEffect(() => {
    if (noPrvToOpenControlSetting && history.location.pathname === AppPaths.controlView.path.base) {
      history.push(AppPaths.pressView.path.base);
    }
  }, [noPrvToOpenControlSetting, history]);

  const reportsConfirmBtn = useCallback(
    (
      title: string,
      contentTitle: string,
      body: JSX.Element,
      btnTitle: string,
      onConfirm: () => void,
      dataTestId: string
    ) => {
      return (
        <ConfirmButton
          promptProps={{
            content: (
              <div className="flex flex-col my-3 mx-5">
                <Typography>{contentTitle}</Typography>
                {body}
              </div>
            ),
            title,
            type: null,
            onConfirm,
          }}
          dataTestId={dataTestId}
        >
          {({ onClick }) => (
            <Button onClick={onClick} style={{ marginBottom: 20 }} dataTestId={`${dataTestId}-button`}>
              {btnTitle}
            </Button>
          )}
        </ConfirmButton>
      );
    },
    []
  );

  const reportsModal = useMemo(
    () => (
      <Dialog open={showModal} classes={{ paper: classes.dialog }} data-testid="sidebar-reports-modal">
        <CustomHeader title={t`Generate Reports`} dataTestId="sidebar-reports-modal-header" />
        <div className="flex flex-col mb-2" data-testid="sidebar-reports-modal-content">
          {accountId &&
            reportsConfirmBtn(
              t({
                message: 'Generate Actuations Report',
                id: '[Report.GenerateActuations]',
              }),
              t({
                message: 'Select range for Actuations report',
                id: '[Report.SelectActuationsRange]',
              }),
              <CustomSelect
                label={null}
                onChange={(selectedValue) => setRangeState((selectedValue as CustomSelectItem).value as RangeType)}
                options={Object.values(RangeType).map((value) => ({
                  label: TimePeriodOptions[mapRangeTypeToTimePeriod(value)].label,
                  value,
                }))}
                minWidth={120}
                className={classes.select}
                value={{
                  label: TimePeriodOptions[mapRangeTypeToTimePeriod(rangeState)].label,
                  value: rangeState,
                }}
                isDisabled={false}
                isError={false}
                dataTestId="sidebar-reports-modal-actuations-report-rangeState-select"
              />,
              t({
                message: 'Actuations Report',
                id: '[Report.Actuations]',
              }),
              () =>
                handleGetActuationsReport({
                  accountId: accountId,
                  userName: name,
                  from: TimePeriodOptions[mapRangeTypeToTimePeriod(rangeState)].range[0],
                  to: TimePeriodOptions[mapRangeTypeToTimePeriod(rangeState)].range[1],
                }),
              'sidebar-reports-modal-actuations-report-confirm-prompt'
            )}
          {accountId &&
            reportsConfirmBtn(
              t({
                message: 'Generate Fleet Report',
                id: '[Report.GenerateFleet]',
              }),
              t({
                message: 'Fleet Report Call In Report Days',
                id: '[Report.NumberOfCallInDays]',
              }),
              <StyledSlider
                minWidth={120}
                value={callInDays}
                onChange={(_, value) => {
                  if (isNil(value)) return;
                  setCallInDays(value as number);
                }}
                step={1}
                name={'callInDays'}
                min={7}
                max={84}
                valueLabelDisplay="on"
                style={{ margin: '20px 0 0' }}
                onResize={undefined}
                onResizeCapture={undefined}
              />,
              t({
                message: 'Fleet Report',
                id: '[Report.Fleet]',
              }),
              () => handleGetFleetReport({ id: accountId, callInDays }),
              'sidebar-reports-modal-fleet-report-confirm-prompt'
            )}
          {accountId &&
            !isPmZone &&
            reportsConfirmBtn(
              t({
                message: 'Generate Maintenance Report',
                id: '[Report.GenerateMaintenance]',
              }),
              t({
                message: 'Generate Maintenace Report',
                id: '[Report.GenerateMaintenanceMessage]',
              }),
              <Trans>Click OK to generate Maintenace Report</Trans>,
              t({
                message: 'Maintenance Report',
                id: '[Report.Maintenance]',
              }),
              () => handleGetMaintenanceReport({ id: accountId }),
              'sidebar-reports-modal-maintenance-report-confirm-prompt'
            )}
        </div>
        <Button variant="outlined" onClick={() => setShowModal(false)} dataTestId="sidebar-reports-modal-close-button">
          <Trans id="[Action.Close]">Close</Trans>
        </Button>
      </Dialog>
    ),
    [
      name,
      isPmZone,
      accountId,
      showModal,
      rangeState,
      callInDays,
      classes.select,
      classes.dialog,
      TimePeriodOptions,
      reportsConfirmBtn,
      handleGetFleetReport,
      handleGetActuationsReport,
      handleGetMaintenanceReport,
    ]
  );

  return (
    <div
      className="flex flex-col justify-start w-full h-full max-h-full max-w-full overflow-hidden"
      data-testid="sidebar-wrapper"
    >
      <CustomHeader title={content.header} buttons={content.buttons} dataTestId="sidebar-header" />
      {showData && (sidebarSection === SiderKeys.accHierarchy || sidebarSection === SiderKeys.pmzDetails) && (
        <div
          className={`flex ${
            isPmZone ? 'justify-between' : 'items-start'
          } w-full max-w-full overflow-x-auto overflow-y-hidden mb-2 ${classes.buttonsWrapper}`}
          data-testid="sidebar-buttons"
        >
          {isPmZone && (
            <Button
              onClick={() => history.push(AppPaths.controlView.path.base)}
              disabled={noPrvToOpenControlSetting}
              dataTestId="sidebar-buttons-control-button"
            >
              <Trans id="[PMZDetailsBtn.Control]">Control</Trans>
            </Button>
          )}
          {isPmZone && (
            <Button
              onClick={() => history.push(AppPaths.graphsView.path.base)}
              dataTestId="sidebar-buttons-graphs-button"
            >
              <Trans id="[PMZDetailsBtn.Graphs]">Graphs</Trans>
            </Button>
          )}
          <Button onClick={() => setShowModal(true)} dataTestId="sidebar-buttons-reports-button">
            <Trans id="[PMZDetailsBtn.Reports]">Reports</Trans>
          </Button>
          {isPmZone && (
            <Button onClick={() => history.push(AppPaths.logsView.path.base)} dataTestId="sidebar-buttons-logs-button">
              <Trans id="[PMZDetailsBtn.Logs]">Logs</Trans>
            </Button>
          )}
          <Button
            onClick={() => history.push(AppPaths.alarmsView.path.base)}
            dataTestId="sidebar-buttons-alarms-button"
          >
            <Trans id="[PMZDetailsBtn.Alarms]">Alarms</Trans>
          </Button>
        </div>
      )}

      {isPmZone !== null && showData && sidebarState ? content.component : <LoadingData dataTestId="sidebar-loading" />}

      {reportsModal}
    </div>
  );
};
